import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Observable, of, Subject, switchMap, tap} from 'rxjs';
import {environment} from 'src/environments/environment';
import {
  IDateNotifications,
  IFilteredNotifications,
  INotifficationUpdateBody,
  INotifficationUpdateStatus,
  IRoleNotification,
  ISalaryNotification,
} from '../interfaces/approval-notifications.interfaces';
import {
  IAccountCredentials,
  IWorkAccount,
} from '../interfaces/account.interfaces';

@Injectable({
  providedIn: 'root',
})
export class ApprovalNotificationService {
  public countOfNotifications = new Subject<number>();
  private url = `${environment.API}api/v1/Notification/`;
  private urlSalary = `${environment.API}api/v1/Salary/Increase/Decision`;
  private urlRole = `${environment.API}api/v1/Request/RoleRequest/Decision`;
  private urlAccount = `${environment.API}api/v1/Employee/Account/`;

  constructor(private http: HttpClient) {}

  getNotifications(period: number): Observable<IFilteredNotifications> {
    return this.http.get<IFilteredNotifications>(`${this.url}${period}`);
  }

  updateRoleNotification(
    body: INotifficationUpdateBody,
    period: number
  ): Observable<IFilteredNotifications> {
    return this.http
      .post<void>(this.urlRole, body)
      .pipe(switchMap(() => this.getNotifications(period)));
  }

  updateSalaryNotification(
    body: INotifficationUpdateBody
  ): Observable<INotifficationUpdateStatus> {
    return this.http.post<INotifficationUpdateStatus>(this.urlSalary, body);
  }

  countUnreadNotifications(): Observable<number> {
    return this.http
      .get<number>(this.url + 'Unread/Count')
      .pipe(tap(count => this.countOfNotifications.next(count)));
  }

  countActiveNotifications(value: IDateNotifications[]): void {
    const allRoleNotifications: IRoleNotification[] = [];
    const allSalaryNotifications: ISalaryNotification[] = [];

    for (const item of value) {
      if (item.roleNotifications) {
        allRoleNotifications.push(...item.roleNotifications);
      }
      if (item.salaryIncreaseNotifications) {
        allSalaryNotifications.push(...item.salaryIncreaseNotifications);
      }
    }
    const countRolesActiveNotification = allRoleNotifications.filter(
      (item: IRoleNotification) => item.status === 'Pending'
    ).length;
    const countSalaryActiveNotification = allSalaryNotifications.filter(
      (item: ISalaryNotification) => item.status === 'Pending'
    ).length;

    this.countOfNotifications.next(
      countRolesActiveNotification + countSalaryActiveNotification
    );
  }

  updateReadStatus(): Observable<void> {
    return this.http.put<void>(this.url + 'ReadStatus/Update', {});
  }

  public getAccountCredentials(employeeId: number): Observable<IWorkAccount> {
    return this.http.get<IWorkAccount>(`${this.urlAccount}${employeeId}`);
  }

  public createWorkAccount(
    data: IWorkAccount,
    period: number
  ): Observable<IFilteredNotifications> {
    return this.http
      .post<IFilteredNotifications>(`${this.urlAccount}Create`, data)
      .pipe(switchMap(() => this.getNotifications(period)));
  }

  public cancelAccountCreation(
    notificationId: number,
    period: number
  ): Observable<IFilteredNotifications> {
    return this.http
      .put<IFilteredNotifications>(
        `${this.urlAccount}${notificationId}/Cancel`,
        {}
      )
      .pipe(switchMap(() => this.getNotifications(period)));
  }

  public sendAccountCredentials(
    data: IAccountCredentials,
    notificationId: number,
    period: number
  ): Observable<IFilteredNotifications> {
    data.notificationId = notificationId;
    return this.http
      .post<IFilteredNotifications>(`${this.urlAccount}SendCredentials`, data)
      .pipe(switchMap(() => this.getNotifications(period)));
  }
}
